import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import './home.css';
import {
  BoltIcon,
  BottomNav,
  BUYER_NAV,
  CardIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ColorRail,
  HatchIcon,
  HistoryIcon,
  HomeAssistant,
  LeafIcon,
  LockIcon,
  PickupIcon,
  PiggyIcon,
  ScaleIcon,
  SearchIcon,
  SedanIcon,
  ShieldTrustIcon,
  SparkleIcon,
  SuvIcon,
  TagIcon,
  TopBar,
  VanIcon,
} from '../../../commodity/components/caria';
import { fetchCatalogFeed } from '../api/catalog.api';
import { VehicleSummary } from '../types';
import { useAuthChrome } from '../../../core/identity-access/hooks/useAuthChrome';

/**
 * Home — buyer landing, ported 1:1 from
 * docs/html-prototypes/CAR-IA/car-ia-home.html: hero + dark search panel,
 * category chip strip, "Descubre por tipo", "Compra por precio", budget chips,
 * the pricing-guide teaser, the dark "¿Por qué CAR-IA?" panel, the free-to-use
 * closer and the footer.
 *
 * The one addition to the prototype is "Destacados" — the live /catalog feed
 * that already ships (BC-07-E01). It uses the prototype's card language and
 * self-hides when the feed is empty.
 */

/* ---- Prototype data (car-ia-home.html) ---------------------------------- */

/** BCRD reference rate used by the price grid, as in the prototype. */
const RATE = 63.5;
const fmt = (n: number) => n.toLocaleString('es-DO');

type IconCmp = React.FC<{ size?: number | string; strokeWidth?: number }>;

const CHIPS: [IconCmp, string][] = [
  [BoltIcon, 'Eléctricos'],
  [LeafIcon, 'Híbridos'],
  [SparkleIcon, 'Nuevos'],
  [HistoryIcon, 'Usados'],
  [SuvIcon, 'SUVs'],
  [PickupIcon, 'Camionetas'],
  [VanIcon, 'Minivans'],
  [PiggyIcon, 'Bajo RD$1MM'],
  [ShieldTrustIcon, 'Tasados'],
];

/** [icon, label, count, hiddenBehindShowMore, countOverride] */
const TYPES: [IconCmp, string, number, boolean, string?][] = [
  [SuvIcon, 'SUVs', 847, false],
  [PickupIcon, 'Camionetas', 412, false],
  [SedanIcon, 'Sedanes', 623, false],
  [SuvIcon, 'Jeepetas', 298, false],
  [HatchIcon, 'Hatchbacks', 186, false],
  [PiggyIcon, 'Económicos', 523, false, 'menos de US$15k'],
  [VanIcon, 'Minivans', 144, true],
  [SedanIcon, 'Coupés', 97, true],
  [BoltIcon, 'Eléctricos', 63, true],
  [LeafIcon, 'Híbridos', 128, true],
  [VanIcon, 'Furgonetas', 81, true],
  [PickupIcon, 'Chasis', 39, true],
];

const PRICES = [
  400000, 600000, 800000, 1000000, 1500000, 2000000, 2500000, 3000000, 4000000, 5000000, 7000000,
  10000000,
];

const BUDGETS: [IconCmp, string][] = [
  [CardIcon, 'Cuota mensual'],
  [PiggyIcon, 'Bajo RD$800k'],
  [TagIcon, 'Bajo RD$1.5MM'],
  [ScaleIcon, 'Bajo RD$2.5MM'],
  [SparkleIcon, 'Bajo RD$4MM'],
  [SearchIcon, 'Sin límite'],
];

const GUIDE: [string, string, string][] = [
  ['Toyota Hilux 2022', 'RD$ 2.32 — 2.67M', 'rango justo · mediana 2.50M'],
  ['Mazda CX-5 2021', 'RD$ 1.69 — 1.95M', 'rango justo · mediana 1.82M'],
  ['Hyundai Tucson 2022', 'RD$ 1.75 — 2.01M', 'rango justo · mediana 1.88M'],
  ['Toyota Corolla 2023', 'RD$ 1.50 — 1.72M', 'rango justo · mediana 1.61M'],
];

const WHY: [IconCmp, string, string][] = [
  [
    ShieldTrustIcon,
    'Señales de confianza — sabes en qué confiar',
    'Cada anuncio muestra tasación, inspección, récord y verificación del vendedor. Confianza en datos, no en promesas.',
  ],
  [
    ScaleIcon,
    'Señales de decisión — paga el precio justo',
    'Tasador virtual + tasador real + rango de mercado. Sabes si estás pagando de más antes de ofertar.',
  ],
  [
    LockIcon,
    'Información pareja — sin sorpresas',
    'Historial, rango de precio y reputación del vendedor a la vista de las dos partes. La asimetría de información se queda fuera del trato. Privacidad por defecto.',
  ],
];

const TABS: [string, string, string][] = [
  ['buscar', 'Buscar carro', 'Buscar por marca, modelo o año…'],
  ['vender', 'Vender mi carro', 'Ingresa tu placa o chasis…'],
  ['resenas', 'Leer reseñas', 'Buscar reseñas por modelo…'],
];

const FOOTER: [string, [string, string][]][] = [
  [
    'Comprar',
    [
      ['Buscar vehículos', '/search'],
      ['Tasación', '/tasacion'],
      ['Financiamiento', '/financiamiento'],
      ['Guía de precios', '/guia-precios'],
    ],
  ],
  [
    'Vender',
    [
      ['Publicar anuncio', '/publish'],
      ['Mis anuncios', '/my-listings'],
      ['Tasar mi carro', '/tasacion'],
    ],
  ],
  [
    'Mi cuenta',
    [
      ['Mis compras', '/compras'],
      ['Guardados', '/saved'],
      ['Mis contactos', '/my-contacts'],
    ],
  ],
  [
    'CAR-IA',
    [
      ['Cómo funciona', '/como-funciona'],
      ['Compra segura', '/compra-segura'],
      ['Centro de ayuda', '/ayuda'],
      ['Términos', '/legal'],
    ],
  ],
];

/* ---- Destacados helpers (live feed) ------------------------------------- */

const FALLBACK = [
  'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=500&h=300&fit=crop&q=80',
  'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=500&h=300&fit=crop&q=80',
  'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=500&h=300&fit=crop&q=80',
  'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=500&h=300&fit=crop&q=80',
];
const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const img = (v: VehicleSummary, i: number) =>
  v.heroImageUrl && !v.heroImageUrl.includes('X-Amz-Stub')
    ? v.heroImageUrl
    : FALLBACK[i % FALLBACK.length];
/**
 * Count of granted trust signals — NOT a percentage (DEF-005). The 0-100 Trust
 * Score is BC-03-E05 / BC-07-E09, deferred T1; rendering granted÷total as a
 * percentage overstated verified trust, especially since two of the four marks
 * are granted by hand.
 */
const trustSignals = (v: VehicleSummary) => {
  const m = v.trustMarks ?? [];
  return m.length ? `${m.filter((x) => x.on).length}/${m.length}` : '—';
};

export const Home: React.FC = () => {
  const authChrome = useAuthChrome();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState<VehicleSummary[]>([]);
  const [tab, setTab] = useState(0);
  const [q, setQ] = useState('');
  const [allTypes, setAllTypes] = useState(false);
  const [allPrices, setAllPrices] = useState(false);

  useEffect(() => {
    let active = true;
    fetchCatalogFeed({ sort: 'newest', limit: 8 })
      .then((r) => active && setFeatured(r.vehicles))
      .catch(() => active && setFeatured([]));
    return () => {
      active = false;
    };
  }, []);

  const search = () => navigate(q.trim() ? `/search?q=${encodeURIComponent(q.trim())}` : '/search');

  const types = useMemo(() => TYPES.filter(([, , , hidden]) => allTypes || !hidden), [allTypes]);
  const prices = useMemo(() => (allPrices ? PRICES : PRICES.slice(0, 8)), [allPrices]);

  return (
    <div className="cia-home">
      <TopBar {...authChrome} nav={BUYER_NAV} />
      <ColorRail />

      {/* ---------- HERO ---------- */}
      <section className="cia-home-hero">
        <div className="cia-home-wrap">
          <h1>
            Compra, vende.
            <br />
            Todo en un solo lugar.
          </h1>
        </div>
        <div className="cia-home-panel">
          <div className="cia-home-ptabs" role="tablist">
            {TABS.map(([key, label], i) => (
              <button
                key={key}
                role="tab"
                aria-selected={tab === i}
                className={`cia-home-ptab${tab === i ? ' on' : ''}`}
                onClick={() => setTab(i)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="cia-home-psearch">
            <input
              type="search"
              value={q}
              placeholder={TABS[tab][2]}
              aria-label="Buscar vehículos"
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
            />
            <button className="cia-home-pgo" aria-label="Buscar" onClick={search}>
              <SearchIcon size={20} />
            </button>
          </div>
          <div className="cia-home-phelp">
            <span>o deja que te ayudemos</span>
            <button onClick={() => navigate('/search')}>Encontrar un carro</button>
          </div>
        </div>
      </section>

      {/* ---------- CATEGORY CHIPS ---------- */}
      <div className="cia-home-chipstrip">
        <div className="cia-home-chiprow">
          {CHIPS.map(([Icon, label]) => (
            <button key={label} className="cia-home-cchip" onClick={() => navigate('/search')}>
              <Icon size={17} strokeWidth={1.7} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ---------- DESTACADOS (live) ---------- */}
      {featured.length > 0 && (
        <section className="cia-home-sec">
          <div className="cia-home-wrap">
            <div className="cia-home-sech">
              <h2>Destacados para ti</h2>
              <button className="cia-home-minilink" onClick={() => navigate('/search')}>
                Ver todos →
              </button>
            </div>
            <p className="cia-home-secsub">Los mejores deals de tu feed esta semana</p>
            <div className="cia-home-fgrid">
              {featured.map((v, i) => {
                const deal =
                  v.suggestedPrice && v.sellerPrice <= v.suggestedPrice * 0.97
                    ? { label: 'Buen precio', warn: false }
                    : v.suggestedPrice && v.sellerPrice >= v.suggestedPrice * 1.03
                    ? { label: 'Sobre mercado', warn: true }
                    : null;
                return (
                  <button
                    key={v.id}
                    className="cia-home-fcard"
                    onClick={() => navigate(`/vehicles/${v.id}`)}
                  >
                    <div
                      className="cia-home-fphoto"
                      style={{ backgroundImage: `url('${img(v, i)}')` }}
                    >
                      <span className="cia-home-ftrust">✓ {trustSignals(v)} señales</span>
                    </div>
                    <div className="cia-home-finfo">
                      <div className="cia-home-fprice-row">
                        <span className="cia-home-fprice">{usd(v.sellerPrice)}</span>
                        {deal && (
                          <span className={`cia-home-fdeal${deal.warn ? ' warn' : ''}`}>
                            {deal.label}
                          </span>
                        )}
                      </div>
                      <div className="cia-home-ftitle">
                        {v.make} {v.model} {v.year}
                      </div>
                      <div className="cia-home-fmeta">
                        {v.mileageKm.toLocaleString('en-US')} km · {v.region ?? '—'}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ---------- DESCUBRE POR TIPO ---------- */}
      <section className="cia-home-sec">
        <div className="cia-home-wrap">
          <div className="cia-home-sech">
            <h2>Descubre por tipo</h2>
          </div>
          <p className="cia-home-secsub">
            Encuentra por tipo de vehículo · <span className="tnum">3,089</span> disponibles ahora
          </p>
          <div className="cia-home-typegrid">
            {types.map(([Icon, label, count, , alt]) => (
              <button key={label} className="cia-home-tcard" onClick={() => navigate('/search')}>
                <span className="cia-home-tic">
                  <Icon size={23} strokeWidth={1.7} />
                </span>
                <b>{label}</b>
                <small className="tnum">{alt ?? `${fmt(count)} disponibles`}</small>
              </button>
            ))}
          </div>
          <button
            className={`cia-home-morebtn${allTypes ? ' open' : ''}`}
            onClick={() => setAllTypes((v) => !v)}
          >
            <span>{allTypes ? 'Ver menos' : 'Ver todos los tipos'}</span>
            <ChevronDownIcon size={16} />
          </button>
        </div>
      </section>

      {/* ---------- COMPRA POR PRECIO ---------- */}
      <section className="cia-home-sec alt">
        <div className="cia-home-wrap">
          <div className="cia-home-sech">
            <h2>Compra por precio</h2>
          </div>
          <p className="cia-home-secsub">
            Tasa referencial BCRD · <span className="tnum">RD$ 63.50 = US$ 1.00</span>
          </p>
          <div className="cia-home-pricegrid">
            {prices.map((p) => (
              <button key={p} className="cia-home-pcard" onClick={() => navigate('/search')}>
                <span>
                  <b className="tnum">Bajo RD$ {fmt(p)}</b>
                  <small className="tnum">≈ US$ {fmt(Math.round(p / RATE / 100) * 100)}</small>
                </span>
                <ChevronRightIcon size={16} />
              </button>
            ))}
          </div>
          <button
            className={`cia-home-morebtn${allPrices ? ' open' : ''}`}
            onClick={() => setAllPrices((v) => !v)}
          >
            <span>{allPrices ? 'Ver menos' : 'Ver todos los rangos'}</span>
            <ChevronDownIcon size={16} />
          </button>
        </div>
      </section>

      {/* ---------- PRESUPUESTO ---------- */}
      <section className="cia-home-sec">
        <div className="cia-home-wrap">
          <div className="cia-home-sech">
            <h2>Encuentra modelos que se ajusten a ti</h2>
          </div>
          <p className="cia-home-secsub">Dinos tu presupuesto y te mostramos lo que alcanzas</p>
          <div className="cia-home-budget">
            {BUDGETS.map(([Icon, label]) => (
              <button key={label} className="cia-home-bchip" onClick={() => navigate('/search')}>
                <Icon size={24} strokeWidth={1.8} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- GUÍA DE PRECIOS ---------- */}
      <section className="cia-home-sec">
        <div className="cia-home-wrap">
          <div className="cia-home-sech">
            <h2>Guía de precios CAR-IA</h2>
            <button className="cia-home-minilink" onClick={() => navigate('/guia-precios')}>
              Ver la guía completa →
            </button>
          </div>
          <p className="cia-home-secsub">
            El rango justo de cada modelo, con la misma data que calcula el ribbon de valor.
            Consulta antes de ofertar — o antes de poner precio.
          </p>
          <div className="cia-home-gpg">
            {GUIDE.map(([model, range, note]) => (
              <button
                key={model}
                className="cia-home-gpc"
                onClick={() => navigate('/guia-precios')}
              >
                <small>{model}</small>
                <b className="tnum">{range}</b>
                <span>{note}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- POR QUÉ CAR-IA ---------- */}
      <section className="cia-home-sec alt">
        <div className="cia-home-wrap">
          <div className="cia-home-why">
            <div>
              <h2>
                ¿Por qué <em>CAR-IA</em>?
              </h2>
              <p className="cia-home-wlede">
                El marketplace que protege tu inversión. Datos verificables antes de que hagas una
                oferta, no promesas.
              </p>
            </div>
            <div className="cia-home-wlist">
              {WHY.map(([Icon, title, body]) => (
                <div key={title} className="cia-home-witem">
                  <span className="cia-home-wic">
                    <Icon size={19} strokeWidth={1.8} />
                  </span>
                  <div>
                    <b>{title}</b>
                    <p>{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- CIERRE ---------- */}
      <div className="cia-home-freebar">
        <h3>Gratis para todos</h3>
        <p>Sin comisiones para comprar. Sin costo para explorar. Sin letra chica.</p>
        <button className="cia-home-cta" onClick={() => navigate('/search')}>
          Explorar vehículos
          <ChevronRightIcon size={16} />
        </button>
      </div>

      {/* ---------- FOOTER ---------- */}
      <footer className="cia-home-foot">
        <div className="cia-home-footin">
          {FOOTER.map(([heading, links]) => (
            <div key={heading}>
              <b>{heading}</b>
              {links.map(([label, to]) => (
                <button key={label} onClick={() => navigate(to)}>
                  {label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </footer>

      <HomeAssistant />
      <BottomNav />
    </div>
  );
};

export default Home;
